<template>
  <div class="withdraw-page">
    <div class="panel-page">
      <div class="panel-heading">
        <h2>提现</h2>
      </div>

      <el-row :gutter="16">
        <el-col :span="15">
          <div class="form-card">
            <p class="card-title">提现申请</p>

            <el-form ref="form" :model="form" :rules="rules" label-width="110px">
              <el-form-item label="可提现余额">
                <span class="balance">{{ config.incomeBalanceStr || '0.00' }} 元</span>
                <p v-if="Number(config.incomeFrozen || 0) > 0" class="frozen-tip">
                  另有冻结中收益 {{ config.incomeFrozenStr || '0.00' }} 元（{{ config.frozenCount || 0 }} 笔）<template v-if="config.nextUnfreezeTime">，最早 {{ formatTime(config.nextUnfreezeTime) }} 解冻</template>
                </p>
              </el-form-item>
              <el-form-item label="收款账户" prop="bankId">
                <el-select v-model="form.bankId" placeholder="请选择收款账户" style="width: 100%">
                  <el-option
                    v-for="item in banks"
                    :key="item.id"
                    :label="`${typeText(item.accountType)} ${item.accountNoMask}（${item.accountName}）`"
                    :value="item.id" />
                </el-select>
                <p v-if="banks.length === 0" class="link-tip">
                  还没有收款账户，<a @click="goBank">去添加</a>
                </p>
              </el-form-item>
              <el-form-item label="提现金额" prop="amountYuan">
                <el-input v-model="form.amountYuan" placeholder="请输入提现金额">
                  <template slot="append">元</template>
                </el-input>
                <el-button type="text" size="mini" class="all-btn" @click="fillAll">全部提现</el-button>
              </el-form-item>

              <el-form-item label="费用试算">
                <div class="calc-box">
                  <p>提现金额：￥{{ preview.amount }}</p>
                  <p>提现手续费（{{ config.feeRateStr || '0%' }}）：-￥{{ preview.fee }}</p>
                  <p class="actual">实际到账：￥{{ preview.actual }}</p>
                </div>
              </el-form-item>

              <el-form-item>
                <el-button type="primary" :loading="submitting" @click="submit">提交提现申请</el-button>
                <el-button @click="resetForm">重 置</el-button>
              </el-form-item>
            </el-form>
          </div>
        </el-col>

        <el-col :span="9">
          <div class="rule-card">
            <p class="card-title">提现规则</p>
            <ul>
              <li>收益到账：订单收益先进入「冻结中」，冻结期满（平台当前设置 <b>{{ config.unfreezeDays || 0 }} 天</b>）后转为可提现余额</li>
              <li>单笔最低提现：<b>{{ config.minAmountStr || '0.00' }}</b> 元</li>
              <li>提现手续费：<b>{{ config.feeRateStr || '0%' }}</b>（从提现金额中扣除）</li>
              <li>每月最多可申请：<b>{{ Number(config.monthLimit) === 0 ? '不限' : config.monthLimit + ' 次' }}</b></li>
              <li>提现需平台人工审核，审核通过后线下打款，通常 1-3 个工作日到账</li>
              <li>驳回或撤销的申请，金额将全额退回可提现余额</li>
            </ul>
            <p class="rule-tip">请确保收款账户信息准确，因信息错误导致的打款失败由开发者自行承担。</p>
          </div>
        </el-col>
      </el-row>

      <div class="record-box">
        <p class="card-title">提现记录</p>
        <el-table v-loading="loading" :data="records" style="width: 100%">
          <el-table-column label="提现单号" prop="withdrawNo" width="200" />
          <el-table-column label="提现金额（元）" prop="amountStr" width="120" />
          <el-table-column label="手续费（元）" prop="feeStr" width="110" />
          <el-table-column label="实际到账（元）" width="120">
            <template slot-scope="scope">
              <span class="actual">{{ scope.row.actualAmountStr }}</span>
            </template>
          </el-table-column>
          <el-table-column label="收款账户" min-width="180">
            <template slot-scope="scope">
              {{ typeText(scope.row.accountType) }} {{ scope.row.accountNoMask }}
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="scope">
              <el-tag :type="statusTag(scope.row.status)" size="small">{{ statusText(scope.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="驳回原因" prop="auditRemark" min-width="140" show-overflow-tooltip />
          <el-table-column label="申请时间" width="170">
            <template slot-scope="scope">{{ formatTime(scope.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column label="操作" width="110">
            <template slot-scope="scope">
              <el-button v-if="Number(scope.row.status) === 0" type="text" size="mini" @click="cancel(scope.row)">撤销</el-button>
              <el-button v-if="Number(scope.row.status) === 2" type="text" size="mini" icon="el-icon-view" @click="showDetail(scope.row)">详情</el-button>
            </template>
          </el-table-column>
        </el-table>

        <div v-if="!loading && records.length === 0" class="empty-box">
          <i class="el-icon-tickets empty-icon"></i>
          <p>还没有提现记录</p>
        </div>

        <el-pagination
          v-if="total > 0"
          class="pager"
          background
          layout="total, prev, pager, next"
          :total="total"
          :current-page.sync="queryParams.pageNum"
          :page-size="queryParams.pageSize"
          @current-change="loadRecords" />
      </div>

      <!-- 提现单详情 -->
      <el-dialog title="提现单详情" :visible.sync="detailOpen" width="640px" append-to-body>
        <el-descriptions :column="2" border size="medium">
          <el-descriptions-item label="提现单号">{{ detail.withdrawNo }}</el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusTag(detail.status)" size="small">{{ statusText(detail.status) }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="申请金额">￥{{ detail.amountStr || '0.00' }}</el-descriptions-item>
          <el-descriptions-item :label="detail.feeRateStr ? '手续费（' + detail.feeRateStr + '）' : '手续费'">
            ￥{{ detail.feeStr || '0.00' }}
          </el-descriptions-item>
          <el-descriptions-item label="实际到账">￥{{ detail.actualAmountStr || '0.00' }}</el-descriptions-item>
          <el-descriptions-item label="收款方式">{{ typeText(detail.accountType) }}</el-descriptions-item>
          <el-descriptions-item label="收款人">{{ detail.accountName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="收款账号">{{ detail.accountNoMask || '-' }}</el-descriptions-item>
          <el-descriptions-item label="开户行" :span="2">{{ detail.bankName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="申请时间">{{ formatTime(detail.createdAt) }}</el-descriptions-item>
          <el-descriptions-item label="审核时间">{{ detail.auditTime ? formatTime(detail.auditTime) : '-' }}</el-descriptions-item>
          <el-descriptions-item label="审核意见" :span="2">{{ detail.auditRemark || '-' }}</el-descriptions-item>
          <el-descriptions-item label="打款时间">{{ detail.payTime ? formatTime(detail.payTime) : '-' }}</el-descriptions-item>
          <el-descriptions-item label="打款备注">{{ detail.payRemark || '-' }}</el-descriptions-item>
        </el-descriptions>
        <div v-if="detail.payVoucher" class="voucher-box">
          <p class="voucher-tip">打款凭证</p>
          <el-image
            style="width: 200px; height: 130px"
            :src="voucherSrc(detail.payVoucher)"
            :preview-src-list="[voucherSrc(detail.payVoucher)]"></el-image>
        </div>
        <div slot="footer" class="dialog-footer">
          <el-button @click="detailOpen = false">关 闭</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import {
  getWithdrawConfig,
  listBankAccounts,
  applyWithdraw,
  listWithdraws,
  cancelWithdraw
} from '@/api/developer'
import { dateFormat } from '@/utils/common'

export default {
  name: 'withdrawPage',
  data () {
    return {
      submitting: false,
      loading: true,
      config: {},
      banks: [],
      records: [],
      total: 0,
      // 提现单详情
      detailOpen: false,
      detail: {},
      form: {
        bankId: undefined,
        amountYuan: ''
      },
      rules: {
        bankId: [{ required: true, message: '请选择收款账户', trigger: 'change' }],
        amountYuan: [
          { required: true, message: '请输入提现金额', trigger: 'blur' },
          { pattern: /^\d+(\.\d{1,2})?$/, message: '金额只能为数字，最多两位小数', trigger: 'blur' }
        ]
      },
      queryParams: {
        pageNum: 1,
        pageSize: 10
      }
    }
  },
  computed: {
    preview () {
      const amount = Number(this.form.amountYuan || 0)
      const feeRate = Number(this.config.feeRate || 0)
      // 与后端一致：手续费 = 金额 * 费率(万分比) / 10000，向下取整（单位：分）
      const amountFen = Math.round(amount * 100)
      const feeFen = Math.floor(amountFen * feeRate / 10000)
      const actualFen = amountFen - feeFen
      return {
        amount: (amountFen / 100).toFixed(2),
        fee: (feeFen / 100).toFixed(2),
        actual: (actualFen / 100).toFixed(2)
      }
    }
  },
  methods: {
    typeText (type) {
      const map = { 1: '支付宝', 2: '微信', 3: '银行卡' }
      return map[Number(type)] || '其他'
    },
    statusText (status) {
      const map = { 0: '待审核', 1: '待打款', 2: '已完成', 3: '已驳回', 4: '已取消' }
      return map[Number(status)] || '-'
    },
    statusTag (status) {
      const map = { 0: 'warning', 1: 'primary', 2: 'success', 3: 'danger', 4: 'info' }
      return map[Number(status)] || 'info'
    },
    formatTime (value) {
      return value ? dateFormat(value, 'YYYY-MM-DD HH:mm') : '-'
    },
    // 查看提现单详情
    showDetail (row) {
      this.detail = Object.assign({}, row)
      this.detailOpen = true
    },
    // 打款凭证地址：相对路径补全为可直接访问的地址
    voucherSrc (url) {
      if (!url) {
        return ''
      }
      if (/^(http|blob|data:)/.test(url)) {
        return url
      }
      return this.BaseUrl + '/' + String(url).replace(/^\/+/, '')
    },
    loadConfig () {
      getWithdrawConfig().then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.config = data || {}
        } else {
          this.$message.error(msg || '获取提现规则失败')
        }
      })
    },
    loadBanks () {
      listBankAccounts().then(res => {
        const { code, data } = res
        if (code === 0) {
          this.banks = Array.isArray(data) ? data : []
          const defaultBank = this.banks.find(item => Number(item.isDefault) === 1)
          if (defaultBank && !this.form.bankId) {
            this.form.bankId = defaultBank.id
          }
        }
      })
    },
    loadRecords () {
      this.loading = true
      listWithdraws(this.queryParams).then(res => {
        const { code, data, msg } = res
        if (code === 0) {
          this.records = data.list || []
          this.total = data.total || 0
        } else {
          this.$message.error(msg || '获取提现记录失败')
        }
      }).finally(() => {
        this.loading = false
      })
    },
    fillAll () {
      this.form.amountYuan = (Number(this.config.incomeBalance || 0) / 100).toFixed(2)
    },
    submit () {
      this.$refs.form.validate(valid => {
        if (!valid) return
        const amountFen = Math.round(Number(this.form.amountYuan) * 100)
        if (amountFen <= 0) {
          this.$message.error('提现金额必须大于 0')
          return
        }
        if (Number(this.config.incomeBalance || 0) < amountFen) {
          this.$message.error('可提现余额不足')
          return
        }
        if (Number(this.config.minAmount || 0) > 0 && amountFen < Number(this.config.minAmount)) {
          this.$message.error(`单笔提现金额不得低于 ${this.config.minAmountStr} 元`)
          return
        }
        this.submitting = true
        applyWithdraw({ bankId: this.form.bankId, amount: amountFen }).then(res => {
          const { code, data, msg } = res
          if (code === 0) {
            this.$message.success((data && data.msg) || msg || '提现申请已提交')
            this.resetForm()
            this.loadConfig()
            this.loadRecords()
          } else {
            this.$message.error(msg || '提交失败')
          }
        }).finally(() => {
          this.submitting = false
        })
      })
    },
    resetForm () {
      this.form.amountYuan = ''
      this.$refs.form && this.$refs.form.clearValidate()
    },
    cancel (row) {
      this.$confirm(`确认撤销提现单「${row.withdrawNo}」？金额将退回可提现余额。`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        cancelWithdraw(row.id).then(res => {
          const { code, msg } = res
          if (code === 0) {
            this.$message.success(msg || '已撤销')
            this.loadConfig()
            this.loadRecords()
          } else {
            this.$message.error(msg || '撤销失败')
          }
        })
      }).catch(() => {})
    },
    goBank () {
      this.$router.push({ path: 'developerBankAccount' })
    }
  },
  created () {
    this.loadConfig()
    this.loadBanks()
    this.loadRecords()
  }
}
</script>

<style lang="less" scoped>
.withdraw-page {
  .panel-heading h2 {
    margin: 0 0 20px 0;
    font-size: 22px;
    color: #303133;
  }

  .form-card,
  .rule-card,
  .record-box {
    background: #fff;
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 20px;
  }

  .record-box {
    margin-top: 18px;
  }

  .card-title {
    margin: 0 0 18px 0;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
    border-left: 3px solid #0d95e8;
    padding-left: 8px;
  }

  .balance {
    font-size: 18px;
    font-weight: 600;
    color: #0d95e8;
    font-family: 'SF Mono', Consolas, Menlo, monospace;
  }

  .frozen-tip {
    margin: 6px 0 0 0;
    font-size: 12px;
    line-height: 18px;
    color: #e6a23c;
  }

  .link-tip {
    margin: 6px 0 0 0;
    font-size: 12px;
    color: #e6a23c;

    a {
      color: #0d95e8;
      cursor: pointer;
    }
  }

  .all-btn {
    margin-top: 6px;
    padding: 0;
  }

  .calc-box {
    background: #f5f7fa;
    border-radius: 4px;
    padding: 12px 16px;

    p {
      margin: 0;
      font-size: 13px;
      color: #606266;
      line-height: 24px;
    }

    .actual {
      margin-top: 6px;
      padding-top: 6px;
      border-top: 1px dashed #dcdfe6;
      font-size: 15px;
      font-weight: 600;
      color: #f56c6c;
    }
  }

  .rule-card {
    ul {
      margin: 0;
      padding-left: 18px;

      li {
        font-size: 13px;
        color: #606266;
        line-height: 26px;
      }

      b {
        color: #303133;
      }
    }

    .rule-tip {
      margin: 12px 0 0 0;
      padding-top: 10px;
      border-top: 1px dashed #dcdfe6;
      font-size: 12px;
      color: #e6a23c;
      line-height: 20px;
    }
  }

  .actual {
    color: #f56c6c;
    font-weight: 600;
  }

  .empty-box {
    padding: 40px 0;
    text-align: center;
    color: #909399;

    .empty-icon {
      font-size: 44px;
      color: #c0c4cc;
    }

    p {
      margin: 12px 0 0 0;
    }
  }

  .voucher-box {
    margin-top: 16px;

    .voucher-tip {
      margin: 0 0 6px 0;
      font-size: 13px;
      color: #909399;
    }
  }

  .pager {
    margin-top: 16px;
    text-align: right;
  }
}
</style>
